import colors from '@Common/assets/colors';
import Table from '@Common/Table';
import { useGeneratorContext } from '@Context/GeneratorContext';
import { JiraIssue } from '@Models/types';
import { MagnifyingGlass, SquaresFour, X } from '@phosphor-icons/react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { BounceLoader, MoonLoader } from 'react-spinners';

const JiraIssues = () => {
  const {
    jiraIssues,
    selectedJiraProject,
    fetchJiraIssues,
    setSelectedJiraIssue,
    fetchIssueDescrips,
    genPageLoading,
    setGenPageLoading,
    jiraNextPageToken,
    searchJiraIssuesOnServer,
  } = useGeneratorContext();

  const loading = genPageLoading;
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isServerSearching, setIsServerSearching] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<JiraIssue | null>(null);
  const [filteredSearch, setFilteredSearch] = useState<JiraIssue[]>(jiraIssues);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading || isFetchingMore) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          jiraNextPageToken &&
          !search &&
          selectedJiraProject
        ) {
          setIsFetchingMore(true);
          fetchJiraIssues(selectedJiraProject.key, true).finally(() => {
            setIsFetchingMore(false);
          });
        }
      });

      if (node) observer.current.observe(node);
    },
    [
      loading,
      isFetchingMore,
      jiraNextPageToken,
      search,
      selectedJiraProject,
      fetchJiraIssues,
    ]
  );

  useEffect(() => {
    if (!search) {
      setFilteredSearch(jiraIssues);
      setIsServerSearching(false);
      return;
    }

    const lowerSearch = search.toLowerCase();
    const localMatches = jiraIssues.filter(
      (issue: JiraIssue) =>
        issue.summary.toLowerCase().includes(lowerSearch) ||
        issue.key.toLowerCase().includes(lowerSearch)
    );

    if (localMatches.length > 0) {
      setFilteredSearch(localMatches);
      setIsServerSearching(false);
      return;
    }

    if (selectedJiraProject) {
      setIsServerSearching(true);
      setFilteredSearch([]);

      const debounceTimer = setTimeout(async () => {
        const serverResults = await searchJiraIssuesOnServer(
          selectedJiraProject.key,
          search
        );
        setFilteredSearch(serverResults);
        setIsServerSearching(false);
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [search, jiraIssues, selectedJiraProject, searchJiraIssuesOnServer]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    if (loading) {
      setSearch('');
    }
  }, [loading]);

  const clearSearch = () => {
    setSearch('');
    setFilteredSearch(jiraIssues);
  };

  useEffect(() => {
    if (selectedJiraProject) {
      setGenPageLoading(true);
      fetchJiraIssues(selectedJiraProject.key).finally(() =>
        setGenPageLoading(false)
      );
    }
  }, [selectedJiraProject, setGenPageLoading]);

  const columns: {
    header: string;
    accessor: keyof JiraIssue;
    Cell?: (
      value: JiraIssue[keyof JiraIssue],
      row: JiraIssue
    ) => React.ReactNode;
  }[] = [
    { header: 'Summary', accessor: 'summary' },
    {
      header: 'Key',
      accessor: 'key',
      Cell: (value: JiraIssue[keyof JiraIssue]) => (
        <span className="whitespace-nowrap font-bold">
          {typeof value === 'string' ? value : null}
        </span>
      ),
    },
  ];

  const handleSelectIssue = (issue: JiraIssue) => {
    setSelectedRow(issue);
    setSelectedJiraIssue(issue);

    // 2. Triggers the adapter mapped fetch for the Requirements panel
    fetchIssueDescrips(issue.key);
  };

  return (
    <div
      data-testid="test-jira-issues"
      className="flex h-full flex-col rounded-lg bg-white"
    >
      <span className="flex w-full flex-col items-start justify-between gap-y-2 rounded-t-md bg-primary-700 px-4 py-2 sm:flex-row">
        <span className="font-extrabold text-white">Issues</span>

        <div className="flex w-full flex-row items-center gap-x-2 rounded-md border bg-white px-2 py-1 sm:w-56">
          <MagnifyingGlass size={20} weight="bold" />
          <input
            disabled={loading}
            type="text"
            className={`w-full border-gray-200 font-inter text-sm focus:outline-none ${loading && 'animate-pulse'}`}
            placeholder={loading ? 'Loading Jira Issues' : 'Search Jira Issue'}
            value={search}
            onChange={handleSearch}
          />

          {loading ? (
            <BounceLoader
              data-testid="test-issue-search-loading"
              size={20}
              color={colors.brandWarning[500]}
            />
          ) : (
            <X
              data-testid="test-clear-btn"
              weight="bold"
              className="cursor-pointer hover:text-brandRed-500"
              onClick={clearSearch}
            />
          )}
        </div>
      </span>

      <div className="mx-2 mb-2 flex h-0 flex-grow flex-col">
        {/* NO PROJECT SELECTED */}
        {!selectedJiraProject && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={100} weight="bold" />
            <label className="text-xs text-neutral-500">
              Select a project first
            </label>
          </span>
        )}

        {/* NO ISSUES AVAILABLE */}
        {!loading && selectedJiraProject && jiraIssues.length === 0 && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={100} weight="bold" />
            <label className="text-xs text-neutral-500">
              No Issues Available
            </label>
          </span>
        )}

        {/* LOADING STATE */}
        {loading && selectedJiraProject ? (
          <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
            <MoonLoader color="#071D49" />
            <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
              Jira Issues Loading
            </span>
          </span>
        ) : (
          <>
            {/* ISSUES TABLE */}
            {filteredSearch && filteredSearch.length > 0 && (
              <>
                <Table
                  columns={columns}
                  data={filteredSearch}
                  selectedRow={selectedRow}
                  rowClickHandler={handleSelectIssue}
                  lastRowRef={lastElementRef}
                />
                {isFetchingMore && (
                  <div className="test-gray-500 flex flex-row justify-center gap-4 py-2 text-center text-xs">
                    Fetching more issues...
                    <BounceLoader
                      data-testid="test-issue-search-loading"
                      size={15}
                      color={colors.brandWarning[500]}
                    />
                  </div>
                )}
              </>
            )}

            {/* SERVER FALLBACK LOADING STATE */}
            {isServerSearching && (
              <span className="flex h-full flex-col items-center justify-center gap-y-4 text-neutral-300">
                <BounceLoader size={40} color={colors.brandWarning[500]} />
                <label className="animate-pulse text-xs text-neutral-500">
                  Searching server for "{search}"...
                </label>
              </span>
            )}

            {/* NO SEARCH RESULTS */}
            {selectedJiraProject &&
              !loading &&
              !isServerSearching && // Ensure we aren't displaying this while searching
              jiraIssues.length > 0 &&
              filteredSearch.length === 0 && (
                <span className="flex h-full flex-col items-center justify-center text-neutral-300">
                  <SquaresFour size={100} weight="bold" />
                  <label className="text-xs text-neutral-500">
                    No Issues Found
                  </label>
                </span>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default JiraIssues;
